import { Link, useLocation } from "react-router-dom";
import { BoostSpeedButton } from "@/components/boost-speed/BoostSpeedButton";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { Menu, X } from "lucide-react";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import itqanLogo from "@/assets/itqan-logo-new.png";
import { useIsMobile } from "@/hooks/useMediaQuery";

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isMobile = useIsMobile();

  const navItems = [
    { label: "الخدمات", href: "/services" },
    { label: "أعمالنا", href: "/work" },
    { label: "كيف نعمل", href: "/how-we-work" },
    { label: "الموارد", href: "/resources" },
    { label: "المدونة", href: "/blog" },
    { label: "من نحن", href: "/about" },
    { label: "الأسئلة", href: "/faq" },
    { label: "تواصل", href: "/contact" },
  ];

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  return (
    <>
      <header className="fixed top-0 w-full z-50 glass border-b border-foreground/10 h-[84px] md:h-[104px]">
        <nav className="container mx-auto px-4 h-full flex items-center" aria-label="التنقل الرئيسي">
          {/* Desktop Layout: 3-column grid */}
          <div className="hidden lg:grid lg:grid-cols-3 lg:items-center lg:gap-4 w-full">
            {/* Left: Controls */}
            <div className="flex justify-start gap-1 mr-8">
              <div className="scale-90">
                <ThemeToggle />
              </div>
              <div className="scale-90">
                <BoostSpeedButton />
              </div>
            </div>

            {/* Center: Logo */}
            <div className="flex justify-center mr-6">
              <Link 
                to="/" 
                className="group relative hover:opacity-90 transition-all duration-300"
                aria-label="إتقان - الصفحة الرئيسية"
              >
                <div className="relative">
                  {/* Animated gradient background */}
                  <div className={cn(
                    "absolute -inset-4 bg-gradient-to-r from-primary/10 via-accent/15 to-primary/10 rounded-2xl opacity-60 group-hover:opacity-100 transition-all duration-500 animate-pulse",
                    isMobile ? "blur-sm" : "blur-2xl"
                  )} />
                  
                  {/* Shimmer effect */}
                  <div className={cn(
                    "absolute -inset-2 bg-gradient-to-r from-transparent via-primary/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 animate-shimmer",
                    isMobile ? "blur-sm" : "blur-lg"
                  )} />
                  
                  {/* Glow rings - disabled on mobile */}
                  {!isMobile && (
                    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-primary/30 via-accent/30 to-sand/20 blur-xl opacity-0 group-hover:opacity-80 transition-opacity duration-500 scale-110" />
                  )}
                  
                  <img 
                    src={itqanLogo} 
                    alt="إتقان"
                    width="192"
                    height="192"
                    className="relative h-[115px] w-auto object-contain transition-all duration-500 group-hover:scale-110 group-hover:drop-shadow-[0_0_30px_rgba(90,168,255,0.8)] filter group-hover:brightness-110"
                  />
                </div>
              </Link>
            </div>

            {/* Right: Navigation */}
            <div className="flex items-center justify-end gap-1 flex-nowrap">
              {navItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    "text-sm font-medium transition-all duration-500 ease-out relative group px-2 py-1.5 rounded-lg",
                    location.pathname === item.href
                      ? "text-sand"
                      : "text-foreground/80 hover:text-sand hover:bg-primary/5"
                  )}
                >
                  {/* Glow effect on hover */}
                  <span className="absolute inset-0 rounded-lg bg-gradient-to-r from-primary/0 via-primary/10 to-accent/0 opacity-0 group-hover:opacity-100 transition-all duration-700 blur-sm" />
                  
                  {/* Text with smooth scale */}
                  <span className="relative inline-block transform scale-[0.89] group-hover:scale-[0.95] transition-transform duration-300">
                    {item.label}
                  </span>
                  
                  {/* Enhanced underline animation */}
                  <span className={cn(
                    "absolute -bottom-0.5 left-1/2 -translate-x-1/2 h-[1.5px] bg-gradient-to-r from-primary via-sand to-accent rounded-full transition-all duration-500 ease-out shadow-lg shadow-primary/50",
                    location.pathname === item.href 
                      ? "w-[80%] opacity-100" 
                      : "w-0 opacity-0 group-hover:w-[80%] group-hover:opacity-100"
                  )} />
                  
                  {/* Corner accents */}
                  {location.pathname === item.href && (
                    <>
                      <span className="absolute top-0 right-0 w-0.5 h-0.5 rounded-full bg-primary animate-pulse" />
                      <span className="absolute bottom-0 left-0 w-0.5 h-0.5 rounded-full bg-accent animate-pulse delay-100" />
                    </>
                  )}
                </Link>
              ))}
            </div>
          </div>

          {/* Mobile Layout */}
          <div className="lg:hidden flex items-center justify-between w-full">
            {/* Mobile Menu Button - Right side in RTL */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={cn(
                "relative z-50 p-2.5 rounded-lg transition-all duration-300",
                mobileMenuOpen 
                  ? "bg-sand text-obs hover:bg-sand-600" 
                  : "hover:bg-foreground/5 text-foreground"
              )}
              aria-label={mobileMenuOpen ? "إغلاق القائمة" : "فتح القائمة"}
              aria-expanded={mobileMenuOpen}
              type="button"
            >
              <div className="relative w-7 h-7">
                <Menu 
                  className={cn(
                    "absolute inset-0 w-7 h-7 transition-all duration-300",
                    mobileMenuOpen ? "opacity-0 rotate-90 scale-50" : "opacity-100 rotate-0 scale-100"
                  )} 
                />
                <X 
                  className={cn(
                    "absolute inset-0 w-7 h-7 transition-all duration-300",
                    mobileMenuOpen ? "opacity-100 rotate-0 scale-100" : "opacity-0 -rotate-90 scale-50"
                  )} 
                />
              </div>
            </button>

            {/* Center: Logo */}
            <Link 
              to="/" 
              className="group hover:opacity-90 transition-all duration-300 mr-12"
              onClick={() => setMobileMenuOpen(false)}
              aria-label="إتقان - الصفحة الرئيسية"
            >
              <div className="relative">
                {/* Animated gradient background */}
                <div className={cn(
                  "absolute -inset-4 bg-gradient-to-r from-primary/10 via-accent/15 to-primary/10 rounded-2xl opacity-60 group-hover:opacity-100 transition-all duration-500 animate-pulse",
                  isMobile ? "blur-sm" : "blur-2xl"
                )} />
                
                {/* Shimmer effect */}
                <div className={cn(
                  "absolute -inset-2 bg-gradient-to-r from-transparent via-primary/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 animate-shimmer",
                  isMobile ? "blur-sm" : "blur-lg"
                )} />
                
                {/* Glow rings - disabled on mobile */}
                {!isMobile && (
                  <div className="absolute inset-0 rounded-full bg-gradient-to-br from-primary/30 via-accent/30 to-sand/20 blur-xl opacity-0 group-hover:opacity-80 transition-opacity duration-500 scale-110" />
                )}
                
                <img 
                  src={itqanLogo} 
                  alt="إتقان"
                  width="139"
                  height="139"
                  className="relative h-[83px] w-auto object-contain transition-all duration-500 group-hover:scale-110 group-hover:drop-shadow-[0_0_25px_rgba(90,168,255,0.7)] filter group-hover:brightness-110"
                />
              </div>
            </Link>

            {/* Left: Controls */}
            <div className="flex gap-1 mr-3">
              <ThemeToggle />
              <BoostSpeedButton />
            </div>
          </div>
        </nav>
      </header>

      {/* Mobile Menu Backdrop */}
      <div 
        className={cn(
          "lg:hidden fixed inset-0 bg-background/95 backdrop-blur-md z-40 transition-all duration-300",
          mobileMenuOpen 
            ? "opacity-100 pointer-events-auto" 
            : "opacity-0 pointer-events-none"
        )}
        onClick={() => setMobileMenuOpen(false)}
        aria-hidden="true"
      />

      {/* Mobile Menu Panel */}
      <div 
        className={cn(
          "lg:hidden fixed top-[84px] left-0 right-0 bottom-0 z-40 transition-all duration-300 ease-out",
          mobileMenuOpen 
            ? "translate-x-0 opacity-100" 
            : "-translate-x-full opacity-0"
        )}
      >
        <div className="glass h-full overflow-y-auto">
          <div className="container mx-auto px-4 py-8">
            <nav className="flex flex-col gap-2" aria-label="القائمة المتنقلة">
              {navItems.map((item, index) => (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={cn(
                    "group relative px-6 py-4 rounded-xl text-lg font-medium transition-all duration-500 ease-out overflow-hidden",
                    "hover:bg-sand/10 hover:translate-x-2 hover:shadow-lg hover:shadow-sand/20",
                    location.pathname === item.href
                      ? "bg-gradient-to-l from-sand/20 via-sand/10 to-transparent text-sand shadow-md"
                      : "text-foreground/80 hover:text-sand"
                  )}
                  style={{
                    transitionDelay: mobileMenuOpen ? `${index * 50}ms` : '0ms'
                  }}
                >
                  {/* Animated background gradient */}
                  <span className="absolute inset-0 bg-gradient-to-l from-primary/5 via-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-700 translate-x-[-100%] group-hover:translate-x-0" />
                  
                  {/* Text content */}
                  <span className="relative z-10 inline-block transform group-hover:scale-105 transition-transform duration-300">
                    {item.label}
                  </span>
                  
                  {/* Active indicator with glow */}
                  {location.pathname === item.href && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-sand animate-pulse shadow-lg shadow-sand/50" />
                      <span className="w-1 h-8 bg-gradient-to-b from-sand via-primary to-accent rounded-full shadow-lg shadow-sand/30" />
                    </div>
                  )}
                  
                  {/* Hover arrow indicator */}
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transform translate-x-[-10px] group-hover:translate-x-0 transition-all duration-300">
                    <span className="text-sand text-xl">→</span>
                  </span>
                  
                  {/* Bottom accent line */}
                  <span className="absolute bottom-0 right-0 h-[2px] w-0 bg-gradient-to-l from-sand via-primary to-transparent group-hover:w-full transition-all duration-500 rounded-full" />
                </Link>
              ))}
            </nav>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
